// The signed-in user's own Procurement preferences — today, the daily procurement
// summary's send hour. Backend: modules/procurement/api/settings_routes.py
// (`/settings/user`), whose default mirrors the reminder rule in
// modules/procurement/reminders.py: 07:00 local unless the user says otherwise.
//
// The summary's on/off is NOT here. It is the rule's notification-catalog event
// (`procurement` / `outstanding_processes_digest`), read and written through the
// notification-preferences API — a per-user notification choice has exactly one
// store (docs/platform/notification-settings-placement-design.md § 2).
//
// Consumes the generated orval client (JSON-schema first). The route has no
// response_model, so its read shape is declared here — the bag itself is a
// schemaless JSONB column, validated on the backend by the settings service.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getUserSettingsApiV1ProcurementSettingsUserGet,
  updateUserSettingsApiV1ProcurementSettingsUserPut,
} from '../../../generated/api/procurement-settings/procurement-settings';

/** Local hour the summary goes out when the user has not chosen one. */
export const DEFAULT_DIGEST_SEND_HOUR = 7;

export interface ProcurementUserSettingsBag {
  /** 0–23, in the recipient's own timezone. */
  daily_digest_send_hour?: number;
  // The column is schemaless JSONB and the generated request type is an open
  // record, so the bag stays open here too — the named keys above are the ones
  // this module reads, not the whole set a tenant may carry.
  [key: string]: unknown;
}

export const procurementUserSettingsKey = ['procurement', 'settings', 'user'] as const;

/** The one preference this module reads, narrowed out of the untyped bag.
 *
 * The route carries no `response_model`, so the generated client returns
 * `unknown` — casting that straight to a shape would give the same false
 * confidence as `any`, and the bag really is schemaless JSONB, so a wrong type in
 * it is a live possibility rather than a hypothetical. Reading it through a
 * narrowing parse is what makes the declared types true at runtime. A value of the
 * wrong type is dropped, which lands the caller on the same defaults an absent key
 * would — and the same ones the backend rule applies (see `_digest_send_hour`).
 */
function parseSettings(raw: unknown): ProcurementUserSettingsBag {
  const bag = (raw as { settings?: unknown } | null)?.settings;
  if (typeof bag !== 'object' || bag === null) return {};
  const { daily_digest_send_hour: hour } = bag as ProcurementUserSettingsBag;
  return {
    ...bag,
    daily_digest_send_hour:
      typeof hour === 'number' && Number.isInteger(hour) && hour >= 0 && hour <= 23
        ? hour
        : undefined,
  };
}

export function useProcurementUserSettings() {
  return useQuery({
    queryKey: procurementUserSettingsKey,
    // react-query hands the fetcher a context object, not a bare AbortSignal.
    queryFn: ({ signal }) => getUserSettingsApiV1ProcurementSettingsUserGet(signal),
    select: parseSettings,
  });
}

export function useUpdateProcurementUserSettings() {
  const qc = useQueryClient();
  return useMutation({
    // The PUT replaces the whole bag, so callers merge onto the current settings
    // rather than sending a single key — sending one field alone would drop the
    // others.
    mutationFn: (settings: ProcurementUserSettingsBag) =>
      updateUserSettingsApiV1ProcurementSettingsUserPut({ settings }),
    // Optimistic: a picker that springs back to the old hour for a round-trip and
    // then settles reads as a failed save. The cache holds the RAW response
    // (`select` narrows on read), so this swaps the bag in place rather than
    // asserting the envelope's shape.
    onMutate: async (settings) => {
      await qc.cancelQueries({ queryKey: procurementUserSettingsKey });
      const previous = qc.getQueryData(procurementUserSettingsKey);
      qc.setQueryData(procurementUserSettingsKey, (raw: unknown) =>
        typeof raw === 'object' && raw !== null ? { ...raw, settings } : { settings }
      );
      return { previous };
    },
    onError: (_error, _settings, context) => {
      qc.setQueryData(procurementUserSettingsKey, context?.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: procurementUserSettingsKey }),
  });
}
